import { useId } from 'react';

import { cn } from '../../lib/cn';
import styles from './Logo.module.css';

export type LogoSize = 'sm' | 'md' | 'lg';

export type LogoProps = {
  /** Размер марки; вордмарк масштабируется вместе с ней. */
  readonly size?: LogoSize;
  /** Показать подпись «Tasks!» рядом с маркой. */
  readonly withWordmark?: boolean;
  readonly className?: string;
};

/**
 * Графический логотип Tasks! (docs/06-ui.md §1.1).
 *
 * Марка — не буква в квадрате, а самостоятельный знак: три «колонки доски»
 * разной высоты читаются как стилизованная «T» (левая колонка — ножка,
 * две правые — перекладина), а жёлтый кружок — фирменный акцент Simhub.
 * Градиент есть только у марки: это логотип, а не фон-баннер (docs/06 §1).
 */
export function Logo({ size = 'md', withWordmark = true, className }: LogoProps) {
  // useId даёт уникальный id на каждый экземпляр: без него несколько марок на
  // странице делили бы один defs. Двоеточия React в id убираем — с ними
  // ссылку `url(#…)` надёжнее не строить.
  const gradientId = `logo-gradient-${useId().replace(/:/g, '')}`;

  return (
    <span className={cn(styles['logo'], styles[size], className)}>
      <svg
        aria-hidden
        focusable="false"
        viewBox="0 0 32 32"
        className={styles['mark']}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient
            id={gradientId}
            x1="0"
            y1="0"
            x2="32"
            y2="32"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" className={styles['gradientFrom']} />
            <stop offset="100%" className={styles['gradientTo']} />
          </linearGradient>
        </defs>

        <rect width="32" height="32" rx="9" fill={`url(#${gradientId})`} />
        <rect className={styles['barFull']} x="7" y="8" width="4" height="17" rx="1.6" />
        <rect className={styles['barTall']} x="14" y="8" width="4" height="11" rx="1.6" />
        <rect className={styles['barShort']} x="21" y="8" width="4" height="7" rx="1.6" />
        <circle className={styles['dot']} cx="24" cy="22" r="3" />
      </svg>

      {withWordmark ? <span className={styles['wordmark']}>Tasks!</span> : null}
    </span>
  );
}
